import React from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Checkbox,
  Divider as MuiDivider,
  FormControlLabel,
  FormGroup,
  Grid,
  Link,
  List,
  ListItem,
  TextField,
  Typography as MuiTypography,
} from "@mui/material";
import { spacing } from "@mui/system";

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Typography = styled(MuiTypography)(spacing);

function PoliciesAndProcedures() {
  return (
    <React.Fragment>
      <Helmet title="Policies and Procedures" />

      <Grid container spacing={6} justifyContent="center">
        <Grid item xs={12} lg={9} xl={7}>
          <Typography variant="h2" gutterBottom display="inline">
            Authorization Requirements
          </Typography>
          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/">
              Dashboard
            </Link>
            <Link component={NavLink} to="/documentation/policies-and-procedures">
              Policies And Procedures
            </Link>
            <Typography>Authorization Requirements</Typography>
          </Breadcrumbs>
          <Divider my={6} />
          <Typography variant="body1" paragraph>
            This Authorization Form is designed for use within our SaaS medical application, ensuring that any use or
            disclosure of Protected Health Information (PHI) not otherwise permitted by the Health Insurance Portability
            and Accountability Act (HIPAA) without specific authorization, is conducted in full compliance with the
            regulations. The form facilitates the process of obtaining explicit consent from individuals or their legal
            representatives for the use or disclosure of their PHI for purposes beyond treatment, payment, or healthcare
            operations, incorporating enhancements for clarity and ease of completion.
          </Typography>
          <Typography variant="h5" gutterBottom>
            Authorization Form for Use or Disclosure of Protected Health Information
          </Typography>
          <Typography variant="body1" paragraph>
            Thank you for taking the time to complete this Authorization Form. Your privacy and the security of your
            health information are of utmost importance to us. This form is designed to provide explicit consent for the
            use or disclosure of your Protected Health Information (PHI) for purposes beyond treatment, payment, or
            healthcare operations, as required by the Health Insurance Portability and Accountability Act (HIPAA).
          </Typography>
          <Typography variant="body1" paragraph>
            By completing this form, you are helping us to ensure that your health information is handled in a secure
            and respectful manner.
          </Typography>
          <Typography variant="body1" paragraph>
            Please fill out this form carefully, and do not hesitate to contact us if you have any questions.
          </Typography>
          <List>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column" sx={{ lineHeight: 2 }}>
                <Typography component="span">
                  <b>I. Identifying Information</b>
                </Typography>
                <TextField fullWidth label="Name of Individual" margin="normal" variant="outlined" />
                <TextField fullWidth label="Date of Birth" margin="normal" variant="outlined" />
                <TextField fullWidth label="Social Security Number" margin="normal" variant="outlined" />
                <TextField fullWidth label="Address" margin="normal" variant="outlined" />
                <Box display="flex" justifyContent="space-between">
                  <TextField fullWidth label="City" margin="normal" variant="outlined" sx={{ marginRight: 2 }} />
                  <TextField fullWidth label="State" margin="normal" variant="outlined" sx={{ marginRight: 2 }} />
                  <TextField fullWidth label="Zip Code" margin="normal" variant="outlined" />
                </Box>
                <TextField fullWidth label="Phone Number" margin="normal" variant="outlined" />
                <TextField fullWidth label="Email Address" margin="normal" variant="outlined" />
              </Box>
            </ListItem>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column" sx={{ lineHeight: 2 }}>
                <Typography component="span" sx={{ mb: 2 }}>
                  <b>II. Authorization</b>
                </Typography>
                <Typography component="span" sx={{ mb: 1 }}>
                  I hereby authorize the use or disclosure of my PHI as described below:
                </Typography>
                <Typography component="span" sx={{ mb: 1 }}>
                  <b>Specific description of the PHI to be used or disclosed:</b>
                </Typography>
                <FormGroup>
                  <FormControlLabel control={<Checkbox />} label="Medical history" />
                  <FormControlLabel control={<Checkbox />} label="Treatment records" />
                  <FormControlLabel control={<Checkbox />} label="Billing records" />
                  <FormControlLabel control={<Checkbox />} label="Insurance information" />
                  <FormControlLabel control={<Checkbox />} label="Other (please specify below)" />
                  <TextField fullWidth label="If other, please specify" margin="normal" variant="outlined" />
                </FormGroup>
                <Typography component="span" sx={{ mb: 1 }}>
                  <b>Persons or class of persons authorized to make the requested use or disclosure:</b>
                </Typography>
                <FormGroup>
                  <FormControlLabel control={<Checkbox />} label="Healthcare providers" />
                  <FormControlLabel control={<Checkbox />} label="Insurance companies" />
                  <FormControlLabel control={<Checkbox />} label="Family members" />
                  <FormControlLabel control={<Checkbox />} label="Legal representatives" />
                </FormGroup>
                <Typography component="span" sx={{ mb: 1 }}>
                  <b>Purpose of the use or disclosure:</b>
                </Typography>
                <FormGroup>
                  <FormControlLabel control={<Checkbox />} label="For treatment purposes" />
                  <FormControlLabel control={<Checkbox />} label="For payment activities" />
                  <FormControlLabel control={<Checkbox />} label="For healthcare operations" />
                  <FormControlLabel control={<Checkbox />} label="For research purposes" />
                  <FormControlLabel control={<Checkbox />} label="Other" />
                  <TextField fullWidth label="If other, please specify" margin="normal" variant="outlined" />
                </FormGroup>
              </Box>
            </ListItem>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column" sx={{ lineHeight: 2 }}>
                <Typography component="span" sx={{ mb: 2 }}>
                  <b>III. Rights of the Individual</b>
                </Typography>
                <Typography component="span">
                  I understand that I have the right to revoke this authorization at any time by notifying the entity
                  responsible for this authorization in writing, except to the extent that action has been taken in
                  reliance on it.
                </Typography>
                <TextField
                  fullWidth
                  label="Signature of Individual or Legal Representative"
                  margin="normal"
                  variant="outlined"
                />
                <TextField fullWidth label="Date" margin="normal" variant="outlined" />
                <TextField
                  fullWidth
                  label="Relationship to Individual (if signed by Legal Representative)"
                  margin="normal"
                  variant="outlined"
                />
              </Box>
            </ListItem>
          </List>
          <Typography variant="h5" gutterBottom>
            Instructions
          </Typography>
          <List>
            <ListItem>
              Please complete all sections of this form to authorize the use or disclosure of your Protected Health
              Information as specified.
            </ListItem>
            <ListItem>
              Once completed, please submit this form to the designated contact person or department as indicated by our
              SaaS application.
            </ListItem>
            <ListItem>
              If you have any questions regarding this form or the authorization process, please contact our privacy
              officer at the provided contact information.
            </ListItem>
          </List>

          <Box display="flex" flexDirection="column" sx={{ mb: 2 }}>
            <Typography variant="h5" gutterBottom>
              Download
            </Typography>
          </Box>
          <Box display="flex" flexDirection="column" sx={{ lineHeight: 2 }}>
            <Typography component="span">To download the Authorization Form, please click the link below:</Typography>
          </Box>
          <List>
            <ListItem>
              <Typography component="span" sx={{ mr: 2 }}>
                [
              </Typography>
              <Link href="/static/docs/HIPAA_Authorization_Form.pdf" target="_blank">
                HIPAA Authorization Form
              </Link>
              <Typography component="span" sx={{ ml: 2 }}>
                {" "}
                ]
              </Typography>
            </ListItem>
          </List>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default PoliciesAndProcedures;
